import React, { ReactNode, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock } from 'lucide-react';
import SiteNav from './SiteNav';
import SiteFooter from './SiteFooter';
import AuroraBackground from './AuroraBackground';
import StatusPill from './StatusPill';
import { ModuleMeta } from '../data/modules';

export default function ModulePageLayout({
  module,
  Icon,
  children,
}: {
  module: ModuleMeta;
  Icon: React.ComponentType<{ size?: number; className?: string }>;
  children: ReactNode;
}) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [module.slug]);

  return (
    <div className="min-h-screen bg-bg">
      <SiteNav />

      <section className="relative py-16 overflow-hidden border-b border-border">
        <AuroraBackground variant="subtle" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="flex items-center gap-3 mb-4">
              <div className={`relative w-12 h-12 rounded-xl bg-gradient-to-br ${module.accent} flex items-center justify-center text-white shadow-[0_0_30px_-6px_rgba(242,202,80,0.5)]`}>
                <Icon size={24} />
              </div>
              <span className="text-xs font-bold uppercase tracking-wide bg-surface1 border border-border px-2 py-1 rounded-full text-textSecondary">
                {module.version}
              </span>
              <StatusPill status={module.status} />
            </div>
            <h1 className="font-display text-4xl font-bold text-textPrimary mb-3">{module.name}</h1>
            <p className="text-xl text-textSecondary max-w-2xl">{module.tagline}</p>
          </motion.div>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 space-y-14">{children}</div>

      <section className="bg-surface1 border-t border-border py-14">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-textPrimary mb-3">
            {module.status === 'Live' ? `${module.shortName} is live on mobile` : `${module.shortName} is coming soon`}
          </h2>
          <p className="text-textSecondary mb-6">
            {module.status === 'Live'
              ? 'Sign up on the Bankably mobile app to start using it — then log in here if you ever need admin access.'
              : "We're building this module now. Join the waitlist and we'll let you know the moment it ships."}
          </p>
          <Link
            to="/wishlist"
            className="inline-flex items-center gap-2 px-8 py-3 bg-gold-gradient text-bg rounded-lg font-semibold hover:opacity-90 transition"
          >
            <Lock size={18} /> Join the Waitlist
          </Link>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}

export function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
      <h2 className="font-display text-2xl font-bold text-textPrimary mb-5">{title}</h2>
      {children}
    </motion.div>
  );
}

export function FeatureGrid({ items }: { items: { title: string; body: string }[] }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
      {items.map((f, i) => (
        <motion.div
          key={f.title}
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: i * 0.06 }}
          className="border border-border bg-surface1 rounded-xl p-5 hover:border-gold/30 hover:-translate-y-0.5 transition-all"
        >
          <h3 className="font-bold text-textPrimary mb-2">{f.title}</h3>
          <p className="text-textSecondary text-sm leading-relaxed">{f.body}</p>
        </motion.div>
      ))}
    </div>
  );
}

export function Steps({ items }: { items: string[] }) {
  return (
    <ol className="relative space-y-6">
      <motion.div
        className="absolute left-4 top-2 bottom-2 w-px bg-border origin-top"
        initial={{ scaleY: 0 }}
        whileInView={{ scaleY: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
      />
      {items.map((s, i) => (
        <motion.li
          key={i}
          initial={{ opacity: 0, x: -8 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ delay: i * 0.1 }}
          className="relative flex gap-4"
        >
          <span className="relative z-10 flex-shrink-0 w-8 h-8 rounded-full bg-gold-gradient text-bg flex items-center justify-center font-bold text-sm">
            {i + 1}
          </span>
          <p className="text-textSecondary pt-1">{s}</p>
        </motion.li>
      ))}
    </ol>
  );
}
